import { ChangeEvent, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { visuallyHidden } from '@mui/utils';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import useToast from '@hooks/useToast';
import {
  addLetterTemplate,
  deleteLetterTemplate,
  LetterTemplate,
  replaceLetterTemplate,
  templateErrorMessage,
} from '@api/letter-templates';
import {
  useLetterTemplates,
  useLetterTemplateTypes,
} from '@features/settings-templates/hooks';

/** Another admin deleted it first: the server answers 404. */
const GONE = 'This template was already removed; the list has been refreshed.';

const DOCX_ACCEPT =
  '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

type Row = {
  key: string;
  typeId: number;
  typeName: string;
  template: LetterTemplate | null;
};

type PendingUpload =
  | { kind: 'add'; typeId: number }
  | { kind: 'replace'; templateId: number };

/**
 * One row per active letter type, with the firm's template where there is
 * one. A template whose type has since been retired still gets a row, so it
 * can be seen and deleted.
 */
function buildRows(
  types: { id: number; name: string }[],
  templates: LetterTemplate[]
): Row[] {
  const byType = new Map(templates.map((t) => [t.letter_type.id, t]));
  const rows: Row[] = types.map((type) => ({
    key: `type-${type.id}`,
    typeId: type.id,
    typeName: type.name,
    template: byType.get(type.id) ?? null,
  }));
  const shown = new Set(types.map((type) => type.id));
  templates
    .filter((t) => !shown.has(t.letter_type.id))
    .forEach((t) =>
      rows.push({
        key: `template-${t.id}`,
        typeId: t.letter_type.id,
        typeName: t.letter_type.name,
        template: t,
      })
    );
  return rows;
}

/**
 * Settings > Templates (ZLAYT-1679): the firm's Word templates, one per
 * letter type. Every member of the firm sees them; the firm's admins (and a
 * solo user) add one for a type with none, replace one with a new .docx and
 * delete one. The server checks every upload; its reason for refusing one
 * is shown as it gave it.
 */
function SettingsTemplates() {
  const { showSuccess, showError } = useToast();
  const typesQuery = useLetterTemplateTypes();
  const typesData = typesQuery.data;
  const on = Boolean(typesData && !typesData.off);
  const listQuery = useLetterTemplates(on);

  const fileInput = useRef<HTMLInputElement>(null);
  // A ref, not state: a cancelled picker fires no change, and the next
  // pick must replace it whatever the render timing.
  const pending = useRef<PendingUpload | null>(null);
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState<LetterTemplate | null>(null);
  // Kept while the dialog fades out, so its title keeps the type name.
  const [deleteName, setDeleteName] = useState('');

  const reload = () =>
    Promise.all([typesQuery.mutate(), listQuery.mutate()]).then(
      () => undefined
    );

  const pickFile = (upload: PendingUpload) => {
    pending.current = upload;
    fileInput.current?.click();
  };

  const onFileChosen = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so choosing the same file again still fires a change.
    event.target.value = '';
    const upload = pending.current;
    pending.current = null;
    if (!file || !upload) return;
    if (!file.name.toLowerCase().endsWith('.docx')) {
      showError('Error', 'Choose a Word file (.docx).');
      return;
    }
    setBusy(true);
    try {
      if (upload.kind === 'add') {
        await addLetterTemplate(upload.typeId, file);
        showSuccess(
          'Template added',
          'New letters of this type will be drafted from it.'
        );
      } else {
        await replaceLetterTemplate(upload.templateId, file);
        showSuccess(
          'Template replaced',
          'Letters already drafted keep the file they were made from.'
        );
      }
    } catch (error) {
      showError(
        'Error',
        templateErrorMessage(error, 'The template could not be saved.', {
          // On Add a 404 means the feature was switched off meanwhile.
          notFound: upload.kind === 'replace' ? GONE : undefined,
        })
      );
    } finally {
      // Stay busy until the rows are fresh, or a stale row offers Add for
      // a type that now has a template.
      await reload();
      setBusy(false);
    }
  };

  const onConfirmDelete = async () => {
    const template = toDelete;
    setToDelete(null);
    if (!template) return;
    setBusy(true);
    try {
      await deleteLetterTemplate(template.id);
      showSuccess(
        'Template deleted',
        'The next letter of this type will ask for a template again.'
      );
    } catch (error) {
      showError(
        'Error',
        templateErrorMessage(error, 'The template could not be deleted.', {
          notFound: GONE,
        })
      );
    } finally {
      // Stay busy until the rows are fresh, or a stale row offers Add for
      // a type that now has a template.
      await reload();
      setBusy(false);
    }
  };

  if (typesQuery.error || listQuery.error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => reload()}>
            Retry
          </Button>
        }
      >
        The firm&apos;s templates could not be loaded.
      </Alert>
    );
  }

  if (!typesData || (on && !listQuery.data)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (typesData.off) {
    return (
      <Typography color="text.secondary">
        Letter templates are not available yet.
      </Typography>
    );
  }

  const { canManage } = typesData;
  const rows = buildRows(typesData.types, listQuery.data ?? []);

  return (
    <Stack spacing={2}>
      <Typography color="text.secondary">
        Zlayt AI drafts a letter from your firm&apos;s template for its type.
        Merge fields are written in the Word file as {'{{ field_name }}'}.
      </Typography>
      {!canManage && (
        <Alert severity="info">
          Only your firm&apos;s admins can add, replace or delete templates.
        </Alert>
      )}
      <input
        ref={fileInput}
        type="file"
        accept={DOCX_ACCEPT}
        hidden
        data-testid="template-file-input"
        onChange={onFileChosen}
      />
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Letter type</TableCell>
              <TableCell>Template</TableCell>
              <TableCell>Saved</TableCell>
              <TableCell>Merge fields</TableCell>
              {canManage && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ key, typeId, typeName, template }) => {
              const version = template?.current_version ?? null;
              return (
                <TableRow key={key}>
                  <TableCell>{typeName}</TableCell>
                  <TableCell>
                    {template ? (
                      <Stack>
                        <span>{version?.original_filename || 'Word file'}</span>
                        {version && (
                          <Typography variant="caption" color="text.secondary">
                            {`Version ${version.number}`}
                          </Typography>
                        )}
                      </Stack>
                    ) : (
                      <Typography color="text.secondary">
                        No template
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {version &&
                      `${version.created_by || 'A former user'}, ${dayjs(
                        version.created_at
                      ).format('MMM D, YYYY')}`}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {(version?.merge_fields ?? []).map((field) => (
                        <Tooltip
                          key={field.name}
                          title={field.label}
                          describeChild
                        >
                          <Chip
                            label={
                              <>
                                {field.name}
                                <Box component="span" sx={visuallyHidden}>
                                  {`: ${field.label}`}
                                </Box>
                              </>
                            }
                            size="small"
                            variant="outlined"
                          />
                        </Tooltip>
                      ))}
                    </Stack>
                  </TableCell>
                  {canManage && (
                    <TableCell align="right">
                      {template ? (
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                        >
                          <Button
                            size="small"
                            disabled={busy}
                            aria-label={`Replace ${typeName} template`}
                            onClick={() =>
                              pickFile({
                                kind: 'replace',
                                templateId: template.id,
                              })
                            }
                          >
                            Replace
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            disabled={busy}
                            aria-label={`Delete ${typeName} template`}
                            onClick={() => {
                              setDeleteName(typeName);
                              setToDelete(template);
                            }}
                          >
                            Delete
                          </Button>
                        </Stack>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={busy}
                          aria-label={`Add template for ${typeName}`}
                          onClick={() => pickFile({ kind: 'add', typeId })}
                        >
                          Add template
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(toDelete)} onClose={() => setToDelete(null)}>
        <DialogTitle>{`Delete the ${deleteName} template?`}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Letters already drafted keep the file they were made from. The next
            letter of this type will ask for a template again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={onConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export default SettingsTemplates;
